<script lang="ts">
  import { onMount } from "svelte";
  import { step, type } from "../../stores/BookingStore";
  import { convert24HoursFormat, isMobile } from "../../helpers";
  import { setBookSessionStorage } from "../../helpers/BookHelper";
  import {
    locationsWithKnifeThrowing,
    locationsWithBigAxeThrowing,
  } from "../../helpers/BookHelper";

  export let location: string;
  export let context = "booking"; // "booking" for Wrapper.svelte, "location" for LocationPricing.svelte
  let walkins: any[] = [];
  let socialLanes: any[] = [];
  let showSocialLanesBookingButton = false;

  // Store the previous location to avoid unnecessary fetches
  let previousLocation = "";

  let socialLanesText: string = "(Hatchet, Knife, Big Axe)";

  // Function to fetch data when location changes
  function fetchLocationData(loc) {
    if (!loc || loc === previousLocation) return;
    previousLocation = loc;

    // Fetch walkins data
    fetch(`/wp-json/badaxe/v1/walkins/${loc.toLowerCase()}`, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      cache: "no-store",
    })
      .then((response) => response.json())
      .then((data) => {
        walkins = data;
      })
      .catch((error) => console.error("Error fetching walkins:", error));

    // Fetch social lanes data
    fetch(`/wp-json/badaxe/v1/social-lanes/${loc.toLowerCase()}`, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      cache: "no-store",
    })
      .then((response) => response.json())
      .then((data) => {
        // socialLanes = Array.isArray(data) ? data : [];

        // Convert object with numeric keys to array if needed
        if (data && typeof data === "object" && !Array.isArray(data)) {
          socialLanes = Object.values(data);
        } else {
          socialLanes = Array.isArray(data) ? data : [];
        }

        showSocialLanesBookingButton =
          Array.isArray(socialLanes) &&
          socialLanes.some((sl) => hasTodaySocialLane(sl));
      })
      .catch((error) => console.error("Error fetching social lanes:", error));
  }

  // Watch for location changes
  $: if (location !== previousLocation) {
    fetchLocationData(location);
  }

  function hasTodaySocialLane(socialLane: {
    date: string;
    from: string;
    lanes: number;
    selected: boolean;
    to: string;
  }) {
    if (!socialLane || !socialLane.date) {
      return false;
    }

    try {
      // Get today's date and reset time to midnight in local timezone
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Parse the date string without adding the Z (UTC) suffix
      // This will interpret the date in local timezone
      const [year, month, day] = socialLane.date.split("-").map(Number);

      // Validate date parts
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return false;
      }

      const slDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date

      const isToday = slDate.toDateString() === today.toDateString();

      return isToday;
    } catch (error) {
      console.error("Error comparing dates:", error);
      return false;
    }
  }

  // Initial fetch on component mount
  onMount(() => {
    if (location) {
      fetchLocationData(location);
    }
  });

  const cleanWalkinsTime = (times: string) => {
    return times.replace(/text-brand/g, "").replace(/font-semibold/g, "");
  };

  const getCurrentDaySocialLanes = (
    socialLanes: Array<{
      date: string;
      from: string;
      lanes: number;
      selected: boolean;
      to: string;
    }>
  ) => {
    // Ensure socialLanes is an array
    if (!Array.isArray(socialLanes)) {
      // If it's an object with numeric keys, convert to array
      if (socialLanes && typeof socialLanes === "object") {
        socialLanes = Object.values(socialLanes);
      } else {
        return [];
      }
    }

    const today = new Date();
    today.setHours(12, 0, 0, 0); // Reset time to compare only dates

    // Filter the array to get only today's social lanes
    const todaySocialLanes = socialLanes.filter((lane) => {
      if (!lane.date) return false;
      const socialLaneDate = new Date(`${lane.date}T12:00:00`);
      const isToday = socialLaneDate.toDateString() === today.toDateString();

      return isToday;
    });

    // Map the filtered lanes to include formatted time ranges
    return todaySocialLanes.map((lane) => ({
      ...lane,
      formattedTime: `${convert24HoursFormat(lane.from)} - ${convert24HoursFormat(lane.to)}`,
    }));
  };

  $: socialLanesText = (() => {
    if (
      locationsWithKnifeThrowing.includes(location.toLowerCase()) &&
      locationsWithBigAxeThrowing.includes(location.toLowerCase())
    ) {
      return "(Hatchet, Knife, Big Axe)";
    }
    if (locationsWithBigAxeThrowing.includes(location.toLowerCase())) {
      return "(Hatchet, Big Axe)";
    }
    if (locationsWithKnifeThrowing.includes(location.toLowerCase())) {
      return "(Hatchet, Knife)";
    }

    return "(Hatchet)";
  })();

  // process the click button
  const onSelectTypeHandler = () => {
    if (context === "location") {
      document
        .getElementById("new-booking")
        ?.scrollIntoView({ behavior: "smooth" });
      // When used in LocationPricing, set step to 2 directly (calendar)
      step.update((s) => {
        return 2;
      });

      // Also update sessionStorage for location context
      const storedBookProcess = sessionStorage.getItem("book_process");
      if (storedBookProcess) {
        const bookProcess = JSON.parse(storedBookProcess);
        bookProcess.step = 2;
        bookProcess.type = "social-lanes";
        bookProcess.showBookingModal = isMobile() ? true : false;
        setBookSessionStorage(bookProcess);
      }
    } else {
      document
        .getElementById("new-booking")
        ?.scrollIntoView({ behavior: "smooth" });
      // When used in Wrapper (booking flow), increment step
      step.update((s) => {
        return s + 1;
      });
    }

    type.update(() => "social-lanes");
  };
</script>

<div
  class="h-section-pricing-mobile sm:h-section-pricing relative px-4 py-4 sm:px-4 sm:py-4 text-center bg-white rounded-xl border-2 border-gray-400 text-white shadow-lg shadow-gray-800 flex flex-col items-center justify-start h-80 mt-4 sm:mt-0 relative"
>
  <h4 class="text-2xl uppercase font-semibold text-black mt-3">Social Lanes</h4>
  <p class="mb-3 inline-block text-black border-b-2 border-yellow-400">
    Shareable Lanes. Guaranteed Time.
  </p>
  <div>
    <p class="text-center mt-4">
      <!-- <span class="text-xs text-black">As low as</span> -->
      <span class="uppercase text-black text-4xl block font-semibold"
        >$36.99 <sup class="text-base -top-4">/person</sup></span
      >
      <span class="block text-black text-sm">1 Hour Booking</span>
      <span class="block text-black text-sm mb-2">{socialLanesText}</span>
      <!-- <span class="block text-sm">30 or 60 Minutes</span> -->
    </p>
    <p class="text-center text-black text-xs">
      Social Lanes follow our walk-in schedule. Guarantee a spot by clicking
      below.
      <span class="bg-yellow-highlight inline-block p-1"
        >Designed for 1-3 people to throw with others!</span
      >
    </p>
  </div>
  <!-- <div class="w-full mt-0 mb-2">
    {#if walkins.length > 0}
      <div
        class="flex flex-row py-1 text-base px-4 mt-1 w-full text-black text-base inline-block bg-yellow-highlight"
      >
        <div
          class="w-32 text-right pr-2 flex flex-col items-end justify-center text-black"
        >
          Book Today:
        </div>
        <div
          class="flex-1 pl-2 text-sm flex flex-col items-center justify-center w-full text-black"
        >
          <div
            class="text-base w-full text-right flex flex-col items-center justify-center text-black !text-[0.9rem]"
          >
            {#if Array.isArray(socialLanes) && getCurrentDaySocialLanes(socialLanes).length > 0}
              {#each getCurrentDaySocialLanes(socialLanes) as lane}
                <div>{lane.formattedTime}</div>
              {/each}
            {:else}
              n/a
            {/if}
          </div>
        </div>
      </div>
    {:else}
      <p class="text-center text-black">No social lanes availables for today</p>
    {/if}
  </div> -->

  <!-- {#if showSocialLanesBookingButton} -->
  <!-- <a
    href={`/walkins?l=${btoa(location)}`}
    class="bg-brand text-white pt-3 pb-2 px-4 uppercase rounded-full hover:bg-red-800 transition ease-in-out font-semibold text-base md:text-base absolute bottom-4 w-48"
    >Book Now</a
  > -->
  <a
    on:click={onSelectTypeHandler}
    class="bg-brand text-white pt-3 pb-2 px-4 uppercase rounded-full hover:bg-red-800 transition ease-in-out font-semibold text-base md:text-base absolute bottom-4 w-48 cursor-pointer"
    >Book Now</a
  >
  <!-- {/if} -->
</div>

<style></style>
